import { Context, OAuth2Providers } from '@z-components/utils/oauth';
import Analytics, { LogOptions } from './analytics';
import { createTelemetryBeacon } from 'utils/telemetry';

interface MakeSocialAnalyticsOptions {
  context: Context;
  jobTypeId: string;
  provider: OAuth2Providers;
}

export default class SocialAnalytics {
  context: Context;
  jobTypeId: string;
  provider: OAuth2Providers;

  private static instance: SocialAnalytics;
  private analyticsLogOptions: LogOptions;

  private constructor(options: MakeSocialAnalyticsOptions) {
    this.context = options.context;
    this.jobTypeId = options.jobTypeId;
    this.provider = options.provider;
    this.analyticsLogOptions = {
      jobTypeId: this.jobTypeId,
      shouldDedupeEventsInSession: true,
      beacon: createTelemetryBeacon({
        // TODO: When introducing a scenario where there can be more than one provider, we should regenerate
        // the instance
        provider: this.provider,
        context: this.context,
        action: 'shouldOverride',
      }),
    };
  }

  static getInstance(options: MakeSocialAnalyticsOptions) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!SocialAnalytics.instance) {
      SocialAnalytics.instance = new SocialAnalytics(options);
    }
    return SocialAnalytics.instance;
  }

  private makeOptionOverrides({ shouldDedupeEventsInSession }: LogOptions) {
    const optionOverrides = { ...this.analyticsLogOptions };
    if (shouldDedupeEventsInSession !== undefined) {
      optionOverrides.shouldDedupeEventsInSession = shouldDedupeEventsInSession;
    }
    return optionOverrides;
  }

  /**
   * denotes social flow started from request form
   */
  socialRequestFlowStarted() {
    // If there previously was a social request flow started for this jobTypeId there would be
    // an existing entry, ensure we unset so we can track a new instance of the user going
    // throw the social portion of the request flow.
    Analytics.unsetAnalyticsActionMap(this.jobTypeId);
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialLoginClicked',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }
  /**
   * denotes NEW client user was registered
   */
  socialRequestNewUserCreated() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialAccountCreated',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  /**
   * denotes an EXISTING client was implicitly linked
   */
  socialRequestUserImplicitlyLinked() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialImplicitLinked',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  /**
   * denotes an EXISTING pro explicitly linked their account via email pw
   */
  socialRequestUserExplicitlyLinked() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialExplicitLink',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  /**
   * denotes a user PASSED the email verify step
   */
  socialRequestEmailVerifyPassed() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialEmailVerifyPassed',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  /**
   * denotes an authenticated user passed the phone step
   */
  socialRequestPhonePassed() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialPhoneNumberPassed',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  /**
   * denotes an authenticated user pressed the submit button which should lead to request submission
   */
  socialSubmitNewRequest() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'submitNewRequest',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  /**
   * denotes a successful request creation originating from a social method
   */
  socialSubmitNewRequestSuccess() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialSubmitNewRequestSuccess',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  socialEmailVerifyViewed() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialEmailVerifyViewed',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  /**
   * Scenario where a user backs out from any part of the social request flow
   */
  socialUserBackedFromFlow() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialUserBackedFromFlow',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }

  /**
   * Records event hits on password reset flow initiated. deduping events by sessions is being skipped
   * because we want to understand how often this is initiated overall.
   */
  socialPasswordResetStarted(options: { shouldDedupeEventsInSession?: boolean } = {}) {
    const analyticsOptions = this.makeOptionOverrides(options);
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialPasswordResetStarted',
        label: this.provider,
      },
      analyticsOptions
    );
  }

  socialPasswordResetCompleted() {
    Analytics.logGA(
      {
        category: Analytics.makeRequestCategoryString(this.jobTypeId),
        action: 'socialPasswordResetCompleted',
        label: this.provider,
      },
      this.analyticsLogOptions
    );
  }
}
